// ════════════════════════════════════════════════════════════════════
// src/storeVisits/form-detailed.js  —  INTEGRATED (Sprint 2.3 part 4)
//
// Replaces the part-1 scaffold. Now wires the full flow:
//   - Draft visit created on leaving the metadata page (POST /api/store-visits)
//   - Autosave: debounced on change, on section change, every 30s
//     (PATCH /api/store-visits/:id) with coalescing so saves never overlap
//   - Submit: validation gate → PATCH status='submitted'
//   - Photo slots call renderPhotoUpload() (photoUpload.js)
//   - Section-1 shift-embed slots call renderShiftEmbed() (elcShiftEmbed.js)
//
// Flow:
//   Page 0  metadata (store, duty mgr, entry/exit, visit type) — no draft yet
//     ↓ Next  → validate store+visit_type → createDraft → visit_id exists
//   Pages 1..14  sections — autosave live, photos + embeds work
//   Final page  overall RAG + action plan + photos → Submit (gated on RAG)
//
// NEEDS BROWSER VERIFICATION (full flow): draft creation, autosave PATCH
// firing, embed field names, photo round-trip, submit. Test sequence in the
// handover note. Each stage is independently observable in the Network tab.
//
// Re: `DB` — bare DB (db.js uses let/const, not on window).
// entry_time/exit_time tagged +04:00 (Dubai) — never naive toISOString.
// ════════════════════════════════════════════════════════════════════

import { SECTIONS, FINAL_FIELDS } from './form-spec.js';
import { renderShiftEmbed } from './elcShiftEmbed.js';
import { renderPhotoUpload, fetchPhotos as fetchVisitPhotos } from './photoUpload.js';
import { loadStores, loadStaff, storeOptions, dutyOptions } from './storeStaffData.js';
import { injectSvrStyles, svrHeaderHtml } from './svrChrome.js';
import { captureGeo, geoFields } from './geo.js';
import { adminToken } from '../shared/admin-token.js';   // v136.12: DB token, then this tab, then the remembered copy (src/shared/admin-token.js)

const RAG_COLORS = {
  green: { bg: '#10b981', label: 'Green' },
  amber: { bg: '#f59e0b', label: 'Amber' },
  red:   { bg: '#ef4444', label: 'Red'   },
};

const AUTOSAVE_INTERVAL_MS = 30000;
const AUTOSAVE_DEBOUNCE_MS = 2000;

function getToken() { return adminToken(); }   // v136.12: DB token, then this tab, then the remembered copy (src/shared/admin-token.js)
function getWorkerUrl() {
  if (typeof DB !== 'undefined' && typeof DB.workerUrl === 'function') return DB.workerUrl();
  return '';
}
function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// datetime-local gives "YYYY-MM-DDTHH:MM" (naive). The AM enters Dubai local
// time, so tag the UTC+4 offset explicitly rather than letting Postgres guess.
function dubaiLocalToIso(localStr) {
  if (!localStr) return null;
  let s = localStr;
  if (/T\d\d:\d\d$/.test(s)) s += ':00';   // add seconds if the input omitted them
  return s + '+04:00';
}

// Inverse of dubaiLocalToIso: a stored ISO timestamp (e.g. "2026-06-10T14:30:00+04:00"
// or a UTC "...Z") back to the naive "YYYY-MM-DDTHH:MM" the datetime-local input wants,
// expressed in Dubai wall-clock time. Used when re-opening a report to edit it so the
// entry/exit fields show the same time the AM originally entered — never naive
// toISOString (that would render UTC and shift the clock back 4h). v126.1
function isoToDubaiLocal(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const date = d.toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai' });       // YYYY-MM-DD
    const time = d.toLocaleTimeString('en-GB', {
      timeZone: 'Asia/Dubai', hour: '2-digit', minute: '2-digit', hour12: false,
    });                                                                            // HH:MM
    return date + 'T' + time;
  } catch (e) { return ''; }
}

// A 401/403 from any SVR call means the manager's 24h admin session has lapsed
// (there is no sliding refresh — see /api/admin/login in worker.js). Tag it so
// callers can show a clear "sign in again" prompt instead of the raw
// "Unauthorized" string, and stop hammering a dead token. Fixes the
// area-manager "Unauthorized" SVR reports (an area manager, v118 — could not start /
// save / submit).
function sessionExpiredError() {
  const e = new Error('SESSION_EXPIRED');
  e.sessionExpired = true;
  return e;
}
// v136.27: a 403 is NOT an expired session (that is a 401): the account is signed in but may not change
// this report. Saying "sign in again" sent an area manager round in circles on a store outside their
// area (21 Oct 2026); now the reason is shown, and signing in again is not offered.
async function notAllowedError(res) {
  let m = '';
  try { const d = await res.json(); m = (d && (d.error || d.message)) || ''; } catch (e) { /* no body */ }
  const e = new Error(m || 'This account cannot change this report. Nothing was changed.');
  e.notAllowed = true;
  return e;
}
async function readApi(res) {
  if (res.status === 401) throw sessionExpiredError();
  if (res.status === 403) throw await notAllowedError(res);
  if (!res.ok) {
    const t = await res.text().catch(function () { return ''; });
    throw new Error(res.status + ': ' + t.slice(0, 160));
  }
  return res.json();
}

async function apiPost(pathStr, payload) {
  const res = await fetch(getWorkerUrl() + pathStr, {
    method: 'POST',
    headers: { 'Authorization': 'Bearer ' + getToken(), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return readApi(res);
}

async function apiPatch(pathStr, payload) {
  const res = await fetch(getWorkerUrl() + pathStr, {
    method: 'PATCH',
    headers: { 'Authorization': 'Bearer ' + getToken(), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return readApi(res);
}

async function apiGet(pathStr) {
  const res = await fetch(getWorkerUrl() + pathStr, {
    headers: { 'Authorization': 'Bearer ' + getToken() },
  });
  return readApi(res);
}


// ──────────────────────────────────────────────────────────────────
// Input renderers (RAG + comment render inline; photos + embeds render
// a placeholder container that a post-render pass populates async)
// ──────────────────────────────────────────────────────────────────

function ragButtons(sectionKey, questionKey, currentValue) {
  return '<div class="svr-seg">' + ['green', 'amber', 'red'].map(function (rag) {
    const sel = currentValue === rag;
    const style = sel
      ? 'background:' + RAG_COLORS[rag].bg + ';border-color:' + RAG_COLORS[rag].bg + ';color:#fff;'
      : '';
    return '<button type="button" class="svr-seg-btn svr-rag-btn" ' +
      'data-section="' + sectionKey + '" data-question="' + questionKey + '" data-value="' + rag + '"' +
      (style ? ' style="' + style + '"' : '') + '>' + RAG_COLORS[rag].label + '</button>';
  }).join('') + '</div>';
}

function commentBox(sectionKey, questionKey, currentValue) {
  return (
    '<textarea class="svr-comment-input svr-input" data-section="' + sectionKey + '" data-question="' + questionKey + '" ' +
      'rows="3" maxlength="500" placeholder="Optional — your notes">' +
      escapeHtml(currentValue || '') +
    '</textarea>'
  );
}

function asyncSlot(kind, key, embedKey) {
  // Placeholder div populated after innerHTML is set. data-* drives the
  // post-render pass.
  return (
    '<div class="svr-async-slot" data-kind="' + kind + '" data-key="' + escapeHtml(key) + '"' +
      (embedKey ? ' data-embed="' + escapeHtml(embedKey) + '"' : '') + '>' +
      '<div style="padding:8px;color:var(--tiny);font-size:13px;">Loading…</div>' +
    '</div>'
  );
}


// renderDetailedForm(rootEl, opts)
//   opts.visitId      — when set, the form opens in EDIT mode against this existing
//                       report (re-open a completed report to correct it). AM/HO only;
//                       the launcher gates who can reach this. v126.1
//   opts.existingData — the full visit row (store, section_data, overall_rag,
//                       action_plan, template_snapshot, status, entry/exit). Pre-seeds
//                       the form so the AM edits in place rather than from blank.
export function renderDetailedForm(rootEl, opts) {
  if (!rootEl) { console.warn('[form-detailed] rootEl required'); return; }
  opts = opts || {};
  const editMode = !!opts.visitId;
  const existing = opts.existingData || {};
  const prevStatus = existing.status || null;   // preserved across the edit, recorded in the audit
  // v124.1: a DRAFT re-opened for editing must stay finishable — Submit it (finalise)
  // or Delete it (bin an abandoned one), not just "Save changes" (which leaves it a
  // draft forever). Completed reports keep the correction-only "Save changes" flow.
  const editingDraft = editMode && prevStatus === 'draft';

  injectSvrStyles();
  rootEl.classList.add('svr-page');
  rootEl.style.background = 'var(--bg)';
  rootEl.style.padding = '0';

  const formState = {
    metadata: {},          // store, duty_manager, entry_time, exit_time, visit_type
    sectionData: {},       // { sectionKey: { questionKey: value } }
    photos: {},            // { sectionKey: [ {id, signed_url} ] }
    overall_rag: null,
    action_plan: '',
    currentPage: 0,        // 0 = metadata, 1..N = sections, N+1 = final
  };

  // ── Edit mode: pre-seed formState from the existing report ──
  if (editMode) {
    formState.metadata = {
      store: existing.store || '',
      duty_manager: existing.duty_manager || '',
      visit_type: existing.visit_type || 'detailed',
      entry_time: isoToDubaiLocal(existing.entry_time),
      exit_time: isoToDubaiLocal(existing.exit_time),
    };
    formState.sectionData = existing.section_data || {};
    formState.overall_rag = existing.overall_rag || null;
    formState.action_plan = existing.action_plan || '';
  }

  let visitId = opts.visitId || null;
  let editDirty = false;   // edit mode only: true once the AM changes something unsaved
  // The template this form renders + snapshots against. Starts as the bundled spec,
  // then is replaced by the admin-editable template from GET /api/visit-template once
  // it loads. Rendering AND the create-time snapshot both read activeSections, so the
  // two can never diverge; createDraft() awaits templateReady before snapshotting, so a
  // report is always frozen against the template the user actually saw. Falls back to
  // the bundled spec if the endpoint is down, so the form always works. v125.2
  // Edit mode renders against the report's FROZEN snapshot (the template it was
  // filled against) — never the live one, so editing the template can't rewrite
  // history. Matches the read-only detail view's behaviour. Legacy rows without a
  // snapshot fall back to the bundled SECTIONS. v126.1
  const editSnapshot = (editMode && Array.isArray(existing.template_snapshot) && existing.template_snapshot.length)
    ? existing.template_snapshot
    : null;
  let activeSections = SECTIONS;
  if (editSnapshot) activeSections = editSnapshot;   // edit mode renders the report's frozen snapshot
  let totalPages = activeSections.length + 2;
  const templateReady = editMode
    ? Promise.resolve()   // edit mode already knows its template — don't refetch the live one
    : (async function loadTemplate() {
        try {
          const tpl = await apiGet('/api/visit-template');
          if (tpl && Array.isArray(tpl.sections) && tpl.sections.length) {
            activeSections = tpl.sections;
            totalPages = activeSections.length + 2;
          }
        } catch (e) {
          console.warn('[form-detailed] template fetch failed, using bundled spec', e);
        }
      })();

  // ── Store + duty-manager dropdown data (cached, loaded once) ──
  let storesData = null;   // [{ name, ... }]   exact stores.name values (FK-safe)
  let staffData = null;    // [{ name, store, ... }]
  (async function loadMeta() {
    try {
      const [st, sf] = await Promise.all([loadStores(), loadStaff()]);
      storesData = st;
      staffData = sf;
      if (formState.currentPage === 0) renderCurrentPage();   // populate the selects
    } catch (e) {
      console.warn('[form-detailed] store/staff load failed', e);
    }
  })();

  // ── Autosave machinery (coalescing trailing save) ──
  let saving = false;
  let dirty = false;
  let debounceTimer = null;
  let intervalId = null;
  let authExpired = false;   // set once the admin session lapses mid-report (E1)

  function buildPayload() {
    return {
      store: formState.metadata.store,
      visit_type: formState.metadata.visit_type,
      duty_manager: formState.metadata.duty_manager || null,
      entry_time: dubaiLocalToIso(formState.metadata.entry_time),
      exit_time: dubaiLocalToIso(formState.metadata.exit_time),
      section_data: formState.sectionData,
      overall_rag: formState.overall_rag,
      action_plan: formState.action_plan || null,
    };
  }

  function setSaveStatus(text, colour) {
    const el = rootEl.querySelector('#svr-save-status');
    if (el) { el.textContent = text; el.style.color = colour || 'var(--tiny)'; }
  }

  function markDirty() {
    if (authExpired) return;   // session is dead — don't queue more dead-token saves
    if (editMode) {
      // Editing a completed report: NO autosave. Changes persist only on an explicit
      // "Save changes", so one PATCH (and one audit row) lands per edit session and a
      // half-finished edit never silently overwrites a live report. Just track the
      // unsaved flag + refresh the Save button's enabled/label state. v126.1
      editDirty = true;
      updateEditSaveButton();
      return;
    }
    dirty = true;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(doSave, AUTOSAVE_DEBOUNCE_MS);
  }

  async function doSave() {
    if (authExpired || !visitId || !dirty) return;
    if (saving) { dirty = true; return; }   // coalesce — re-save when current finishes
    saving = true;
    dirty = false;
    setSaveStatus('Saving…');
    try {
      await apiPatch('/api/store-visits/' + visitId, buildPayload());
      const t = new Date().toLocaleTimeString('en-GB', { timeZone: 'Asia/Dubai', hour: '2-digit', minute: '2-digit' });
      setSaveStatus('Saved ' + t, 'var(--ok)');
    } catch (e) {
      if (e && e.sessionExpired) { onSessionExpired(); return; }
      if (e && e.notAllowed) { teardown(); setSaveStatus(e.message, 'var(--ui-bad)'); return; }   // retrying cannot help
      dirty = true;   // retry on next cycle
      setSaveStatus('Save failed — will retry', 'var(--ui-bad)');
      console.warn('[form-detailed] autosave failed:', e.message);
    } finally {
      saving = false;
      if (dirty && !authExpired) setTimeout(doSave, 1000);   // trailing save for changes during the request
    }
  }

  async function createDraft() {
    await templateReady;   // snapshot must freeze the template the user actually saw
    const visit = await apiPost('/api/store-visits', {
      store: formState.metadata.store,
      visit_type: formState.metadata.visit_type,
      duty_manager: formState.metadata.duty_manager || null,
      entry_time: dubaiLocalToIso(formState.metadata.entry_time),
      status: 'draft',
      // Freeze the template this report is filled against, once, at create.
      // Detailed visits only — short visits have no sections. v125.0
      template_snapshot: formState.metadata.visit_type === 'detailed' ? activeSections : null,
    });
    visitId = visit.id;
    // Start the periodic autosave once a draft exists
    if (!intervalId) intervalId = setInterval(doSave, AUTOSAVE_INTERVAL_MS);
    return visit;
  }

  function teardown() {
    clearTimeout(debounceTimer);
    if (intervalId) { clearInterval(intervalId); intervalId = null; }
  }

  // v124.1: delete a DRAFT from the edit view (AM/HO) so an unfinished report isn't
  // stuck forever. Completed reports are deleted from the reports list, not here.
  // Same soft-delete DELETE the list uses; closes + refreshes on success.
  async function deleteDraft() {
    if (!visitId) return;
    if (typeof window.confirm === 'function' &&
        !window.confirm('Delete this draft report? This cannot be undone.')) return;
    setSubmitMsg('Deleting…', 'var(--learn-grey)');
    try {
      const res = await fetch(getWorkerUrl() + '/api/store-visits/' + encodeURIComponent(visitId), { cache: 'no-store',
        method: 'DELETE',
        headers: { 'Authorization': 'Bearer ' + getToken() },
      });
      if (res.status === 401) throw sessionExpiredError();
      if (res.status === 403) throw await notAllowedError(res);
      if (!res.ok) { const t = await res.text().catch(function () { return ''; }); throw new Error(res.status + ': ' + t.slice(0, 160)); }
      teardown();
      try { window.dispatchEvent(new Event('svr:saved')); } catch (e) { /* list refresh is best-effort */ }
      if (rootEl.parentNode) rootEl.parentNode.removeChild(rootEl);
    } catch (e) {
      if (e && e.sessionExpired) { onSessionExpired(); return; }
      setSubmitMsg('Delete failed: ' + e.message, 'var(--ui-bad)');
    }
  }

  // ── E1: graceful handling when the 24h admin session lapses mid-report ──
  // SVRs are long; if the token expires while the form is open, every save 401s.
  // Instead of the old raw "Unauthorized" + an endless silent retry, pause the
  // autosave, keep the answers on screen, stash a best-effort local backup, and
  // show a clear "sign in again" banner. (Seamless in-place re-login + draft
  // resume needs host-app login wiring — a deliberate follow-up, see PR notes.)
  function backupLocally() {
    try {
      localStorage.setItem('svr_draft_backup', JSON.stringify({
        visitId: visitId,
        page: formState.currentPage,
        metadata: formState.metadata,
        section_data: formState.sectionData,
        overall_rag: formState.overall_rag,
        action_plan: formState.action_plan,
        ts: Date.now(),
      }));
    } catch (e) { /* storage disabled/full — best-effort recovery copy only */ }
  }

  function onSessionExpired() {
    if (authExpired) return;
    authExpired = true;
    teardown();          // stop the 30s autosave + debounce — no more dead-token retries
    backupLocally();
    setSaveStatus('Session expired — saving paused', 'var(--ui-bad)');
    showSessionExpiredBanner();
  }

  function showSessionExpiredBanner() {
    if (rootEl.querySelector('#svr-auth-banner')) return;
    const bar = document.createElement('div');
    bar.id = 'svr-auth-banner';
    bar.style.cssText = 'position:sticky;top:0;z-index:50;background:#b91c1c;color:#fff;' +
      'padding:12px 16px;font-size:14px;line-height:1.45;display:flex;gap:12px;' +
      'align-items:center;flex-wrap:wrap;';
    const msg = document.createElement('span');
    msg.style.cssText = 'flex:1;min-width:200px;';
    msg.textContent = 'Your session has expired, so saving is paused. Your answers are ' +
      'still on screen — please note anything important, then sign in again to continue.';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = 'Sign in again';
    btn.style.cssText = 'background:var(--card);color:var(--danger-deep);border:none;border-radius:8px;' +
      'padding:8px 14px;font-weight:600;cursor:pointer;font-family:inherit;flex:0 0 auto;';
    // Reloading routes through admin.html's load-time expiry check, which clears
    // the dead token and shows the login screen.
    btn.addEventListener('click', function () { try { location.reload(); } catch (e) { /* noop */ } });
    bar.appendChild(msg);
    bar.appendChild(btn);
    rootEl.insertBefore(bar, rootEl.firstChild);
  }

  async function submit() {
    const missing = [];
    if (!formState.metadata.store) missing.push('Store');
    if (!formState.metadata.visit_type) missing.push('Visit type');
    if (!formState.metadata.entry_time) missing.push('Entry time');
    if (!formState.overall_rag) missing.push('Overall RAG');
    if (missing.length) {
      setSubmitMsg('Please complete: ' + missing.join(', '), 'var(--ui-bad)');
      return;
    }
    setSubmitMsg('Submitting…', 'var(--sub)');
    try {
      // Flush any pending edits first, then mark submitted
      dirty = true;
      await doSave();
      // Geotag: record WHERE the AM completed the report (permission-prompted; never
      // blocks the submit — null if denied/unavailable). No geofence by design.
      const geo = await captureGeo();
      await apiPatch('/api/store-visits/' + visitId, Object.assign(buildPayload(), { status: 'submitted' }, geoFields(geo)));
      teardown();
      rootEl.innerHTML =
        '<div class="svr-wrap"><div class="svr-done-wrap">' +
          '<div style="font-size:54px;">✅</div>' +
          '<h2 style="color:var(--text);margin:8px 0 4px;">Visit submitted</h2>' +
          '<p style="color:var(--learn-grey);margin:0 0 20px;">' + escapeHtml(formState.metadata.store) + ' — overall ' +
            escapeHtml(formState.overall_rag) + '.</p>' +
          '<button type="button" id="svr-done" class="svr-btn-primary" style="max-width:200px;margin:0 auto;">Done</button>' +
        '</div></div>';
      const done = rootEl.querySelector('#svr-done');
      if (done) done.addEventListener('click', function () {
        if (rootEl.parentNode) rootEl.parentNode.removeChild(rootEl);
      });
    } catch (e) {
      if (e && e.sessionExpired) { onSessionExpired(); return; }
      setSubmitMsg('Submit failed: ' + e.message, 'var(--ui-bad)');
    }
  }

  function setSubmitMsg(text, colour) {
    const el = rootEl.querySelector('#svr-submit-msg');
    if (el) { el.textContent = text; el.style.color = colour || 'var(--sub)'; }
  }

  // ── Edit mode (re-open a completed report) ──

  // Reflect unsaved state on the persistent header Save button.
  function updateEditSaveButton() {
    const btn = rootEl.querySelector('#svr-edit-save');
    if (!btn) return;
    if (editDirty) {
      btn.disabled = false;
      btn.textContent = 'Save changes';
      btn.style.opacity = '1';
    } else {
      btn.disabled = true;
      btn.textContent = 'Saved';
      btn.style.opacity = '0.55';
    }
  }

  // Pull the report's existing photos once and seed formState.photos (grouped by
  // section) so the photo slots show what's already attached. Photo add/remove still
  // goes straight to the photo endpoints (independent of the form PATCH), so it
  // persists immediately even though the form itself doesn't autosave in edit mode.
  async function loadExistingPhotos() {
    try {
      const all = await fetchVisitPhotos(visitId);
      const byKey = {};
      (all || []).forEach(function (p) {
        const k = p.section_key || 'overall';
        (byKey[k] = byKey[k] || []).push(p);
      });
      formState.photos = byKey;
      renderCurrentPage();   // re-render so the seeded photos appear
    } catch (e) {
      console.warn('[form-detailed] existing photos load failed', e);
    }
  }

  async function saveEdit() {
    if (!visitId) return;
    if (!formState.metadata.store) { setSubmitMsg('Store is required', 'var(--ui-bad)'); return; }
    const headerBtn = rootEl.querySelector('#svr-edit-save');
    if (headerBtn) { headerBtn.disabled = true; headerBtn.textContent = 'Saving…'; }
    setSubmitMsg('Saving changes…', 'var(--sub)');
    try {
      // _audit_edit + _prev_status tell the Worker to write one svr_edit audit row.
      // status is intentionally NOT sent, so a 'reviewed' report stays reviewed and
      // its store_confirmed_at / submitted_at timestamps are left untouched. v126.1
      await apiPatch('/api/store-visits/' + visitId, Object.assign(buildPayload(), {
        _audit_edit: true,
        _prev_status: prevStatus,
      }));
      editDirty = false;
      teardown();
      try { window.dispatchEvent(new CustomEvent('svr:saved')); } catch (e) { /* noop */ }
      rootEl.innerHTML =
        '<div class="svr-wrap"><div class="svr-done-wrap">' +
          '<div style="font-size:54px;">✅</div>' +
          '<h2 style="color:var(--text);margin:8px 0 4px;">Changes saved</h2>' +
          '<p style="color:var(--learn-grey);margin:0 0 20px;">' + escapeHtml(formState.metadata.store) +
            ' — report updated.</p>' +
          '<button type="button" id="svr-done" class="svr-btn-primary" style="max-width:200px;margin:0 auto;">Done</button>' +
        '</div></div>';
      const done = rootEl.querySelector('#svr-done');
      if (done) done.addEventListener('click', function () {
        if (rootEl.parentNode) rootEl.parentNode.removeChild(rootEl);
      });
    } catch (e) {
      if (e && e.sessionExpired) { onSessionExpired(); return; }
      setSubmitMsg('Save failed: ' + e.message, 'var(--ui-bad)');
      if (headerBtn) { headerBtn.disabled = false; headerBtn.textContent = 'Save changes'; }
    }
  }

  // ── Page renderers ──

  function metadataPageHtml() {
    const m = formState.metadata;
    function field(label, name, type, required) {
      return (
        '<div class="svr-field">' +
          '<label class="svr-label">' + label + (required ? ' <span class="svr-req">*</span>' : '') + '</label>' +
          '<input type="' + type + '" class="svr-meta-input svr-input" data-field="' + name + '" ' +
            'value="' + escapeHtml(m[name] || '') + '">' +
        '</div>'
      );
    }
    return (
      '<div class="svr-section-title">Visit details</div>' +
      '<div class="svr-card">' +
        '<div class="svr-field"><label class="svr-label">Store <span class="svr-req">*</span></label>' +
          '<select class="svr-meta-input svr-input" data-field="store">' + storeOptions(storesData, m.store) + '</select></div>' +
        '<div class="svr-field"><label class="svr-label">Duty manager</label>' +
          '<select class="svr-meta-input svr-input" data-field="duty_manager"' + (m.store ? '' : ' disabled') + '>' + dutyOptions(staffData, m.store, m.duty_manager) + '</select></div>' +
        field('Entry time', 'entry_time', 'datetime-local', true) +
        field('Exit time', 'exit_time', 'datetime-local', false) +
        '<div class="svr-field"><label class="svr-label">Visit type <span class="svr-req">*</span></label>' +
          '<div class="svr-seg">' +
          ['detailed', 'short'].map(function (vt) {
            const sel = m.visit_type === vt;
            // Visit type is immutable once a report exists (the two types use different
            // forms) — render the buttons disabled in edit mode. v126.1
            return '<button type="button" class="svr-meta-input svr-seg-btn" data-field="visit_type" data-value="' + vt + '"' +
              (editMode ? ' disabled' : '') + ' ' +
              'style="text-transform:capitalize;' + (sel ? 'background:#16a34a;border-color:#16a34a;color:#fff;' : '') +
              (editMode ? 'opacity:0.6;cursor:not-allowed;' : '') + '">' + vt + '</button>';
          }).join('') +
          '</div>' +
        '</div>' +
      '</div>'
    );
  }

  function sectionPageHtml(section) {
    const data = formState.sectionData[section.key] || {};
    const qs = section.questions.map(function (q) {
      let input;
      if (q.type === 'rag') input = ragButtons(section.key, q.key, data[q.key]);
      else if (q.type === 'comment') input = commentBox(section.key, q.key, data[q.key]);
      else if (q.type === 'photos') input = asyncSlot('photos', section.key);
      else if (q.type === 'shift-embed') {
        input = asyncSlot('embed', q.key, q.embedKey) +
          '<div style="margin-top:12px;">' + ragButtons(section.key, q.key, data[q.key]) + '</div>';
      } else input = '<em style="color:var(--tiny);">?</em>';
      return (
        '<div class="svr-field">' +
          '<label class="svr-label">' + escapeHtml(q.label) + '</label>' +
          input +
        '</div>'
      );
    }).join('');
    return (
      '<div class="svr-section-title">' + escapeHtml(section.title) + '</div>' +
      (section.description ? '<div style="font-size:13px;color:var(--learn-grey);margin:-6px 4px 12px;">' + escapeHtml(section.description) + '</div>' : '') +
      '<div class="svr-card">' + qs + '</div>'
    );
  }

  function finalPageHtml() {
    return (
      '<div class="svr-section-title">Overall assessment</div>' +
      '<div class="svr-card">' +
        '<div class="svr-field"><label class="svr-label">' + escapeHtml(FINAL_FIELDS.overall_rag.label) + ' <span class="svr-req">*</span></label>' +
          ragButtons('__final__', 'overall_rag', formState.overall_rag) + '</div>' +
        '<div class="svr-field"><label class="svr-label">' + escapeHtml(FINAL_FIELDS.action_plan.label) + '</label>' +
          '<textarea class="svr-final-input svr-input" data-field="action_plan" rows="5" maxlength="2000">' + escapeHtml(formState.action_plan || '') + '</textarea></div>' +
        '<div class="svr-field"><label class="svr-label">' + escapeHtml(FINAL_FIELDS.photos.label) + '</label>' +
          asyncSlot('photos', 'overall') + '</div>' +
      '</div>' +
      '<button type="button" id="svr-submit" class="svr-btn-primary">' +
        ((editMode && !editingDraft) ? 'Save changes' : 'Submit visit') + '</button>' +
      (editingDraft
        ? '<button type="button" id="svr-delete-draft" style="margin-top:10px;width:100%;min-height:44px;border:1px solid #dc2626;background:var(--card);color:var(--ui-bad);border-radius:10px;font-size:15px;font-weight:700;font-family:inherit;cursor:pointer;">Delete draft</button>'
        : '') +
      '<div id="svr-submit-msg" class="svr-submit-msg"></div>'
    );
  }

  function renderCurrentPage() {
    let pageHtml;
    if (formState.currentPage === 0) pageHtml = metadataPageHtml();
    else if (formState.currentPage <= activeSections.length) pageHtml = sectionPageHtml(activeSections[formState.currentPage - 1]);
    else pageHtml = finalPageHtml();

    const isFirst = formState.currentPage === 0;
    const isLast = formState.currentPage === totalPages - 1;
    const progress = (formState.currentPage + 1) + ' of ' + totalPages;

    const headerRight = editMode
      ? '<button type="button" id="svr-edit-save" style="min-height:34px;padding:0 14px;border:none;' +
          'background:#16a34a;color:#fff;border-radius:9px;font-size:13px;font-weight:700;font-family:inherit;' +
          'cursor:pointer;opacity:' + (editDirty ? '1' : '0.55') + ';"' + (editDirty ? '' : ' disabled') + '>' +
          (editDirty ? 'Save changes' : 'Saved') + '</button>' +
        '<button type="button" id="svr-close" class="svr-close" aria-label="Close">✕</button>'
      : '<div id="svr-save-status" class="svr-status">' + (visitId ? 'Draft saved' : 'Not saved yet') + '</div>' +
        '<button type="button" id="svr-close" class="svr-close" aria-label="Close">✕</button>';

    rootEl.innerHTML =
      svrHeaderHtml({
        subtitle: editMode ? 'Editing report · page ' + progress : 'Page ' + progress,
        right: headerRight,
      }) +
      '<div class="svr-wrap">' +
        '<div id="svr-page-content">' + pageHtml + '</div>' +
        '<div style="display:flex;justify-content:space-between;gap:12px;margin-top:28px;">' +
          '<button type="button" id="svr-prev" style="min-height:46px;padding:0 20px;border:1.5px solid var(--border-strong);background:var(--card);border-radius:12px;font-size:15px;font-weight:600;font-family:inherit;color:var(--text);cursor:pointer;' +
            (isFirst ? 'opacity:0.4;cursor:not-allowed;' : '') + '"' + (isFirst ? ' disabled' : '') + '>← Previous</button>' +
          (isLast ? '<span></span>' :
            '<button type="button" id="svr-next" style="min-height:46px;padding:0 22px;border:none;background:#16a34a;color:#fff;border-radius:12px;font-size:15px;font-weight:700;font-family:inherit;cursor:pointer;box-shadow:0 1px 3px rgba(22,163,74,0.35);">Next →</button>') +
        '</div>' +
      '</div>';

    wireUpInputs();
    populateAsyncSlots();
    // renderCurrentPage rebuilds rootEl.innerHTML, which drops the expiry banner —
    // re-show it so it survives any re-render that happens after the session lapsed.
    if (authExpired) showSessionExpiredBanner();
  }

  // Populate photo + embed containers after the page DOM exists
  function populateAsyncSlots() {
    rootEl.querySelectorAll('.svr-async-slot').forEach(function (slot) {
      const kind = slot.dataset.kind;
      if (kind === 'embed') {
        const embedKey = slot.dataset.embed;
        const store = formState.metadata.store;
        renderShiftEmbed(embedKey, store, slot);
      } else if (kind === 'photos') {
        const sectionKey = slot.dataset.key;
        renderPhotoUpload({
          visitId: visitId,
          sectionKey: sectionKey,
          container: slot,
          photos: formState.photos[sectionKey] || [],
          onChange: function (photos) { formState.photos[sectionKey] = photos; },
        });
      }
    });
  }

  function wireUpInputs() {
    rootEl.querySelectorAll('.svr-rag-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const sec = btn.dataset.section, q = btn.dataset.question, v = btn.dataset.value;
        if (sec === '__final__') formState.overall_rag = v;
        else { (formState.sectionData[sec] = formState.sectionData[sec] || {})[q] = v; }
        markDirty();
        renderCurrentPage();
      });
    });

    rootEl.querySelectorAll('.svr-comment-input').forEach(function (ta) {
      ta.addEventListener('input', function () {
        const sec = ta.dataset.section, q = ta.dataset.question;
        (formState.sectionData[sec] = formState.sectionData[sec] || {})[q] = ta.value;
        markDirty();
      });
    });

    rootEl.querySelectorAll('.svr-meta-input').forEach(function (el) {
      const field = el.dataset.field;
      if (el.tagName === 'BUTTON') {
        el.addEventListener('click', function () {
          formState.metadata[field] = el.dataset.value;
          markDirty();
          renderCurrentPage();
        });
      } else if (el.tagName === 'SELECT') {
        el.addEventListener('change', function () {
          formState.metadata[field] = el.value;
          markDirty();
          if (field === 'store') {
            // store changed → reset duty manager + re-render so its list refreshes
            formState.metadata.duty_manager = '';
            renderCurrentPage();
          }
        });
      } else {
        el.addEventListener('input', function () {
          formState.metadata[field] = el.value;
          markDirty();
        });
      }
    });

    rootEl.querySelectorAll('.svr-final-input').forEach(function (el) {
      el.addEventListener('input', function () { formState[el.dataset.field] = el.value; markDirty(); });
    });

    const prev = rootEl.querySelector('#svr-prev');
    if (prev) prev.addEventListener('click', function () {
      if (formState.currentPage > 0) { formState.currentPage--; renderCurrentPage(); window.scrollTo({ top: 0, behavior: 'smooth' }); }
    });

    // Persistent header Save button (edit mode only) — saves from ANY page so a
    // one-field correction doesn't mean clicking Next through all 14 sections.
    const editSaveBtn = rootEl.querySelector('#svr-edit-save');
    if (editSaveBtn) editSaveBtn.addEventListener('click', saveEdit);

    const next = rootEl.querySelector('#svr-next');
    if (next) next.addEventListener('click', async function () {
      if (editMode) {
        // Edit mode never creates a draft or autosaves — just turn the page.
        if (formState.currentPage < totalPages - 1) {
          formState.currentPage++; renderCurrentPage(); window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        return;
      }
      // Leaving the metadata page for the first time → create the draft
      if (formState.currentPage === 0 && !visitId) {
        if (!formState.metadata.store || !formState.metadata.visit_type) {
          setSaveStatus('Store and visit type are required', 'var(--ui-bad)');
          return;
        }
        next.disabled = true; next.textContent = 'Creating…';
        try {
          await createDraft();
        } catch (e) {
          if (e && e.sessionExpired) { next.disabled = false; next.textContent = 'Next →'; onSessionExpired(); return; }
          setSaveStatus('Could not start visit: ' + e.message, 'var(--ui-bad)');
          next.disabled = false; next.textContent = 'Next →';
          return;
        }
      } else {
        // Save current page before advancing
        markDirty(); doSave();
      }
      if (formState.currentPage < totalPages - 1) {
        formState.currentPage++; renderCurrentPage(); window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });

    const submitBtn = rootEl.querySelector('#svr-submit');
    if (submitBtn) submitBtn.addEventListener('click', (editMode && !editingDraft) ? saveEdit : submit);
    const delDraftBtn = rootEl.querySelector('#svr-delete-draft');
    if (delDraftBtn) delDraftBtn.addEventListener('click', deleteDraft);

    const close = rootEl.querySelector('#svr-close');
    if (close) close.addEventListener('click', function () {
      if (editMode && editDirty && !window.confirm('Discard your unsaved changes to this report?')) return;
      teardown();
      if (rootEl.parentNode) rootEl.parentNode.removeChild(rootEl);
    });
  }

  renderCurrentPage();
  // Edit mode: pull the report's existing photos in once the form is on screen.
  if (editMode) loadExistingPhotos();
}

window.renderDetailedForm = renderDetailedForm;
